import * as React from "react";
import { observer } from "mobx-react";
import { Route, Routes } from "react-router-dom";
import { message, Row } from "antd";

import { Loader } from "Components/General/Loader";
import { NotFoundPlaceholder } from "Components/General/NotFoundPlaceholder";
import { emailSearchStore } from "../Stores/EmailSearchStore";
// import { EmailTypeStore } from '../Stores/EmailTypeStore';
import { withTranslation, WithTranslation } from "react-i18next";
import { ModalConsumer } from "Components/General/Modal/ModalContext";
import { I18N_NS } from "Config/i18n";
import { EmailSearchResultTable } from "../Components/EmailSearchResultTable";
import { SearchBar } from "Components/General/SearchBar";
import { EmailSearchForm } from "../Components/EmailSearchForm";
import { DetailListActions, DropdownActionConfig } from "Components/General/DetailListActions";
import { emailModalProps } from "../utils/emailModalProps";
import { MoveToProjectModal } from "./MoveToProjectModal";
import { WithModal, withModal } from "Components/General/Modal/withModal";
import { withRouter, WithRouterProps } from "Components/General/WithRouter";
import { EmailSearchResultsDetails } from "./EmailSearchResultsDetails";

interface EmailSearchResultsPageProps extends WithRouterProps, WithTranslation, WithModal {
  accessLevel?: "private" | "project";
  onSearch: (text: string) => void;
}

interface EmailSearchResultsState {
  selectedItems: string[];
  selectedEmails: API_MailResponse[];
}

const LOADING_TIMEOUT = 3; // seconds

export const EmailSearchResultsPage = withModal(
  withRouter(
    withTranslation(I18N_NS.PROJECT)(
      observer(
        class extends React.Component<EmailSearchResultsPageProps, EmailSearchResultsState> {
          loadingMessage;
          constructor(props: EmailSearchResultsPageProps) {
            super(props);
            this.state = {
              selectedItems: [],
              selectedEmails: [],
            };
          }

          componentDidMount() {
            if (emailSearchStore.state === "RAW") {
              this.props.navigate("..", { relative: "path" });
            }
          }

          componentDidUpdate() {
            if (!this.loadingMessage && emailSearchStore.state === "LOADING") {
              this.loadingMessage = message.loading(this.props.t("Loading..."), LOADING_TIMEOUT);
            }

            if (!!this.loadingMessage && emailSearchStore.state === "DONE") {
              this.loadingMessage();
              this.loadingMessage = undefined;
            }
          }

          componentWillUnmount() {
            if (this.loadingMessage) {
              this.loadingMessage();
              this.loadingMessage = undefined;
            }
            emailSearchStore.clear();
            emailSearchStore.setSearchTextValue();
          }

          handleGoBack = () => this.props.navigate(-1);

          handleDownloadPdf = (emailId?: string) => {
            emailSearchStore.downloadPdf(emailId ? [emailId] : this.state.selectedItems);
          };

          /**
           * Because this action is used in Email details but propagated from list - we need to have a possibility
           * to pass emailId from currently viewed email to run this action.
           */
          handleMoveToProject = () => {
            this.props.showModal(MoveToProjectModal, {
              modalProps: {
                footer: null,
                title: this.props.t("Move email to Documents"),
                destroyOnHidden: true,
                style: { minWidth: `70%` },
              },
              componentProps: {
                emailStore: emailSearchStore,
                goBack: this.handleGoBack,
                selectedItems: this.state.selectedEmails,
              },
            });
          };

          get linkedMailToolTipText() {
            const { t } = this.props;
            return (
              <span>
                {t("A")}
                <strong> PDF</strong>
                {t(" version of this email will be stored in the folder view of documents and plans. ")}
                <strong>
                  {t("Who has access there, can then access and use the original email including its attachments. ")}
                </strong>
                {t("The email will be moved into your postbox ")}
                <strong>{t('"Linked emails".')}</strong>
              </span>
            );
          }

          get actionsConfig() {
            const dropdownActions: DropdownActionConfig[] = [
              {
                onClick: this.handleDownloadPdf,
                icon: ["far", "file-pdf"],
                label: this.props.t("Download as PDF"),
                // withTooltip: true
              },
              {
                onClick: this.handleMoveToProject,
                icon: ["far", "folder-tree"],
                label: this.props.t("Link with project..."),
                disabled: this.state.selectedEmails.some((item) => !item.moveToProject),
                message: this.linkedMailToolTipText,
                key: "move",
              },
            ];

            return { primaryActions: [], dropdownActions };
          }

          handleSelectItem = (selectedItems: string[], selectedEmails: API_MailResponse[]) => {
            this.setState({ selectedItems, selectedEmails });
          };

          handleSearch = (subject: string) => {
            if (typeof this.props.onSearch === "function") {
              this.props.onSearch(subject);
            } else {
              emailSearchStore.submitSearch({ subject });
            }
          };

          render() {
            const { t } = this.props;
            if (emailSearchStore.state === "LOADING") {
              return <Loader style={{ minHeight: 280 }} />;
            }

            const { navigate, location } = this.props;
            const type = location.pathname.indexOf("/sent") !== -1 ? "SENT" : "INBOX";
            return (
              <Routes>
                <Route path={`:id`} element={<EmailSearchResultsDetails actionsConfig={this.actionsConfig} />} />
                <Route
                  index
                  element={
                    <>
                      <Row justify="space-between" style={{ flexWrap: "nowrap" }}>
                        <ModalConsumer>
                          {({ showModal }) => (
                            <SearchBar
                              defaultValue={emailSearchStore.searchTextValue}
                              hideClear={true}
                              placeholder={t("Search in subject, text")}
                              onShowSearchModal={() =>
                                showModal(EmailSearchForm, {
                                  modalProps: { ...emailModalProps, title: t("Search emails") },
                                })
                              }
                              onSearch={this.handleSearch}
                            />
                          )}
                        </ModalConsumer>
                      </Row>
                      {emailSearchStore.data?.length === 0 ? (
                        <NotFoundPlaceholder
                          text={t("No emails")}
                          icon="envelope"
                          style={{ transform: `translate(0, 50%)` }}
                        />
                      ) : (
                        <>
                          <Row justify="start" align="middle">
                            <div style={{ marginLeft: 10 }}>
                              <DetailListActions {...this.actionsConfig} />
                            </div>
                          </Row>
                          <h3 className="text-center mb-2">
                            {emailSearchStore.data?.length
                              ? emailSearchStore.data.length === 1
                                ? `1 ${t("email found")}`
                                : `${emailSearchStore.data?.length} ${t("emails found")}`
                              : null}
                          </h3>
                          <div className="flex">
                            <EmailSearchResultTable
                              data={emailSearchStore.data?.slice()}
                              onSelectItem={this.handleSelectItem}
                              selectedItems={this.state.selectedItems}
                              onRowClick={(id: string) => navigate(`./${id}`)}
                              addressKey={type === "SENT" ? ("to" as const) : ("from" as const)}
                            />
                          </div>
                        </>
                      )}
                    </>
                  }
                />
              </Routes>
            );
          }
        }
      )
    )
  )
);
